import {
  DefaultError,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import { useInfiniteHandler, useQueryToast } from 'hooks';
import i18next from 'i18next';
import toast from 'react-hot-toast';
import {
  IInfinitePageParams,
  IInfinitePageStatisticsResponse,
} from 'services/types';
import { apiClient } from 'utils';
import { getDefaultInfinitePageParam } from 'utils/service/defaultInfinitePageParam';
import { useResponseHandler } from 'utils/service/responseHandler';
import {
  ICostResponse,
  ICreateCost,
  IRepaymentCost,
  IUnpaidCost,
  IUnpaidCostStatistic,
  IUpdateCost,
} from './types';

export const useGetLabelCosts = (labelId: string, enabled?: boolean) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<ICostResponse[], DefaultError>({
    queryKey: ['label-costs', labelId],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `Cost/Label`,
        params: { labelId },
      });

      return responseHandler(response, {
        groupName: `useGetLabelCosts - ${labelId}`,
        notFoundPath: '/cost',
      });
    },
    enabled: enabled,
  });
};

export const useGetCost = (id: string, enabled?: boolean) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<ICostResponse, DefaultError>({
    queryKey: ['cost', id],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `Cost/${id}`,
      });

      return responseHandler(response, {
        groupName: `usetGetCost - ${id}`,
        notFoundPath: '/cost',
      });
    },
    enabled: enabled,
  });
};

export const usePostCost = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('usePostCost');
  return useMutation<string, DefaultError, ICreateCost>({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.create.loading', {
          what: i18next.t('cost.what'),
        })
      );

      const response = await apiClient({ method: 'POST', url: 'Cost', body });

      return responseHandler(response, {
        groupName: 'usePostCost',
        notFoundPath: '/cost',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.create.success', {
          what: i18next.t('cost.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const usePutCost = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('usePutCost');

  return useMutation<boolean, DefaultError, IUpdateCost>({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.update.loading', {
          what: i18next.t('cost.what'),
        })
      );

      const response = await apiClient({
        method: 'PUT',
        url: 'Cost',
        body,
      });

      return responseHandler(response, {
        groupName: 'usePutCost',
        notFoundPath: '/cost',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.update.success', {
          what: i18next.t('cost.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const usePutCostFile = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('usePutCostFile');

  return useMutation<boolean, DefaultError, { file: File; id: string }>({
    mutationFn: async ({ file, id }) => {
      loading(
        i18next.t('service-message.add.loading', {
          what: i18next.t('track.file-what'),
        })
      );

      const formData = new FormData();

      formData.append('SettlementCostId', id);
      formData.append('File', file);

      const response = await apiClient({
        method: 'PUT',
        url: 'Cost/File',
        body: formData,
        withContentType: false,
      });

      return responseHandler(response, {
        groupName: 'usePutCostFile',
        notFoundPath: '/cost',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.add.success', {
          what: i18next.t('track.file-what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const useDeleteCost = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('useDeleteCost');

  return useMutation<boolean, DefaultError, string>({
    mutationFn: async id => {
      loading(
        i18next.t('service-message.delete.loading', {
          what: i18next.t('cost.what'),
        })
      );

      const response = await apiClient({
        method: 'DELETE',
        url: `Cost`,
        body: { settlementCostId: id },
      });

      return responseHandler(response, {
        groupName: 'useDeleteCost',
        notFoundPath: '/cost',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.delete.success', {
          what: i18next.t('cost.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const useGetUnpaidCostsStatistics = () => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IUnpaidCostStatistic, DefaultError>({
    queryKey: ['unpaid-costs-stats'],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `Cost/Unpaid`,
        params: {
          itemCount: 1,
          page: 0,
        },
      });

      const value = await responseHandler<
        IInfinitePageStatisticsResponse<IUnpaidCost, IUnpaidCostStatistic>
      >(response, {
        groupName: `useGetUnpaidCostsStatistics`,
      });

      return value.statistics;
    },
  });
};

export const useGetUnpaidCosts = (params?: IInfinitePageParams) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, infiniteHandler } = useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(
    params?.page,
    params?.itemCount
  );

  return useInfiniteQuery<IUnpaidCost[], DefaultError>({
    queryKey: ['unpaid-costs-infinite', params],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: 'GET',
        url: `Cost/Unpaid`,
        params: {
          ...params,
          itemCount,
          page: pageParam,
        },
      });

      const value = await responseHandler<
        IInfinitePageStatisticsResponse<IUnpaidCost, IUnpaidCostStatistic>
      >(response, {
        groupName: `useGetUnpaidCosts`,
      });

      return infiniteHandler<IUnpaidCost>(value);
    },

    initialPageParam: page,
    getNextPageParam: getNextPageParam,
  });
};

export const usePutRepaymentCost = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('usePutRepaymentCost');

  return useMutation<{ value: string }, DefaultError, IRepaymentCost>({
    mutationFn: async body => {
      loading(i18next.t('service-message.cost-repayment.loading'));

      const response = await apiClient({
        method: 'PUT',
        url: `Cost/Repayment/Income`,
        params: body,
      });

      return responseHandler(response, {
        groupName: `usePutRepaymentCost`,
      });
    },
    onSuccess: data => {
      success(i18next.t('service-message.cost-repayment.success'));
    },
    onError: dismiss,
  });
};

